import { useState, useRef } from "react";
import type { FC } from "react";
import clsx from "clsx";
import { ArrowDownUp } from "lucide-react";
import { TabHeader } from "../../components";
import { TableSchema } from "./utils";

interface AboutLinkTableProps {
  isSmall?: boolean;
  tableTitle: string;
  tableHeaderList: string[];
  tableDataList: TableSchema[];
  tableFooter?: string;
}

const AboutLinkTable: FC<AboutLinkTableProps> = (props) => {
  const {
    isSmall = false,
    tableTitle,
    tableHeaderList,
    tableDataList,
    tableFooter,
  } = props;

  const [localTableData, setLocalTableData] = useState(tableDataList);
  const sortRef = useRef("asc");

  const ascSort = (sortBy: keyof TableSchema) => {
    const currentData = [...localTableData];
    currentData.sort((a, b) => (a[sortBy] > b[sortBy] ? 1 : -1));
    sortRef.current = "desc";
    return currentData;
  };

  const descSort = (sortBy: keyof TableSchema) => {
    const currentData = [...localTableData];
    currentData.sort((a, b) => (b[sortBy] > a[sortBy] ? 1 : -1));
    sortRef.current = "asc";
    return currentData;
  };

  const doSorting = (sortKey: keyof TableSchema) => {
    if (sortRef.current === "asc") {
      setLocalTableData(ascSort(sortKey));
    } else {
      setLocalTableData(descSort(sortKey));
    }
  };

  return (
    <div className="w-full">
      <TabHeader
        title={tableTitle}
        className={clsx("!px-2 py-1 border-zinc-400", isSmall && "text-sm")}
      />
      <div className="w-full px-2 py-1">
        <table className="w-full">
          <thead>
            <tr className="bg-[#333f50] text-white">
              {tableHeaderList.map((header: string, index: number) => (
                <th
                  key={index}
                  className="border border-white h-6 group"
                  style={isSmall ? {} : { minWidth: "150px" }}
                  onClick={() => doSorting(header)}
                  data-testid="sort-table-header-cell"
                >
                  <div
                    className={clsx(
                      "flex h-full w-full justify-center items-center gap-5",
                      isSmall && "text-sm"
                    )}
                  >
                    {header}
                    <ArrowDownUp
                      className="min-w-[13px] invisible group-hover:visible"
                      size={16}
                      color="#fff"
                    />
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {localTableData.map((tdata, dindex) => (
              <tr
                key={dindex}
                className={clsx(
                  "h-auto",
                  dindex % 2 === 0 ? "bg-white" : "bg-[#d6dce5]"
                )}
              >
                {Object.keys(tdata).map((tDataKey, kindex) => (
                  <td
                    className={clsx(
                      "h-full border border-white hover:bg-gray-400",
                      isSmall ? "text-xs" : "text-sm"
                    )}
                    key={`${dindex}-${kindex}`}
                  >
                    {tdata[tDataKey]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {tableFooter && (
            <tfoot className="w-full">
              <tr className="bg-[#333f50] text-white">
                <td colSpan={tableHeaderList.length} className="px-2 text-sm">
                  {tableFooter}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {/* This hidden field is to increase the coverage by checking its value for asc/desc sorting functions */}
      <input
        type="hidden"
        value={sortRef.current}
        data-testid="sort-hidden-field"
      />
    </div>
  );
};

export default AboutLinkTable;
